// Lab selections that live in the URL (?root=पढ&tense=bhavishya, ?stem=पुरिस, ?table=amha) so a link can open them.
// The server and the first render use the lab's defaults; the URL is read once the router is ready.
// Writes are shallow replaces: the page never reloads and the back button is not flooded.

import { useRouter } from 'next/router'
import { useCallback } from 'react'

interface UrlQuery {
    ready: boolean
    /** The NFC-normalised value of a query parameter, or null (also null before the router is ready). */
    read: (name: string) => string | null
    write: (patch: Record<string, string | null>) => void
}

export const useUrlQuery = (): UrlQuery => {
    const router = useRouter()
    const { isReady, query } = router

    const read = useCallback((name: string): string | null => {
        if (!isReady) return null

        const value = query[name]

        if (typeof value !== 'string') return null

        const clean = value.normalize('NFC').trim().slice(0, 60)

        return clean || null
    }, [isReady, query])

    const write = useCallback((patch: Record<string, string | null>) => {
        const next: Record<string, string | string[] | undefined> = { ...router.query }

        for (const [key, value] of Object.entries(patch)) {
            if (value === null) delete next[key]
            else next[key] = value
        }

        router.replace({ pathname: router.pathname, query: next }, undefined, { shallow: true, scroll: false }).catch(() => undefined)
    }, [router])

    return { ready: isReady, read, write }
}
